import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TableChartIcon from '@mui/icons-material/TableChart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import dayjs from 'dayjs';

const CompetitionUsageModal = ({
    open,
    onClose,
    competition,
    usageData
}) => {
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Get source configuration
    const getSourceConfig = () => {
        if (competition?.ai_recommended && !competition?.competition_id) {
            return {
                icon: <AutoAwesomeIcon fontSize="small" />,
                label: 'AI Recommended (Unsaved)',
                color: 'info'
            };
        }

        switch (usageData?.source) {
            case 'ai_newsletter':
                return {
                    icon: <AutoAwesomeIcon fontSize="small" />,
                    label: 'AI Generated',
                    color: 'info'
                };
            case 'excel_import':
                return {
                    icon: <TableChartIcon fontSize="small" />,
                    label: 'Excel Import',
                    color: 'secondary'
                };
            case 'manual':
            default:
                return {
                    icon: <PersonIcon fontSize="small" />,
                    label: 'Database',
                    color: 'default'
                };
        }
    };

    // Get exclusion status configuration
    const getExclusionConfig = () => {
        if (!usageData?.used_at) {
            return {
                icon: <CheckCircleOutlineIcon fontSize="small" />,
                label: 'New - Never Used',
                color: 'success'
            };
        }

        if (usageData?.is_within_exclusion) {
            return {
                icon: <BlockIcon fontSize="small" />,
                label: `Excluded (${usageData.days_until_reusable}d remaining)`,
                color: 'warning'
            };
        }

        return {
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            label: 'Available',
            color: 'success'
        };
    };

    const sourceConfig = getSourceConfig();
    const exclusionConfig = getExclusionConfig();
    const newsletterHistory = usageData?.newsletter_history || [];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon />
                    Competition Usage History
                </Box>
            </DialogTitle>

            <DialogContent>
                {/* Competition Name */}
                <Typography variant="h6" gutterBottom>
                    {competition?.title || 'Untitled Competition'}
                </Typography>

                {/* Status Badges */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                    <Chip
                        icon={sourceConfig.icon}
                        label={sourceConfig.label}
                        color={sourceConfig.color}
                        size="small"
                    />
                    <Chip
                        icon={exclusionConfig.icon}
                        label={exclusionConfig.label}
                        color={exclusionConfig.color}
                        size="small"
                    />
                </Box>

                {/* Dates Section */}
                {usageData?.used_at && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Last Used:</strong> {dayjs(usageData.used_at).format('MMMM D, YYYY')}
                        </Typography>
                        {usageData.exclusion_expires_at && (
                            <Typography variant="body2" color={usageData.is_within_exclusion ? 'warning.main' : 'text.secondary'}>
                                <strong>
                                    {usageData.is_within_exclusion ? 'Exclusion Expires:' : 'Exclusion Expired:'}
                                </strong> {dayjs(usageData.exclusion_expires_at).format('MMMM D, YYYY')}
                            </Typography>
                        )}
                    </Box>
                )}

                {!usageData?.used_at && competition?.competition_id && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="success.main">
                            This competition has never been used in a published newsletter and is available for selection.
                        </Typography>
                    </Box>
                )}

                {!competition?.competition_id && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="info.main">
                            This is an AI-recommended competition that hasn't been saved to the database yet.
                        </Typography>
                    </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Newsletter History */}
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon fontSize="small" />
                    Newsletter History ({newsletterHistory.length})
                </Typography>

                {newsletterHistory.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Month/Year</TableCell>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Sent</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {newsletterHistory.map((nl, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            {monthNames[nl.month - 1]} {nl.year}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                {nl.title}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {nl.sent_at ? (
                                                dayjs(nl.sent_at).format('MMM D, YYYY')
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    Not sent
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            This competition hasn't been used in any published newsletters yet.
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CompetitionUsageModal;
